import React from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Timezone } from '../../../../state/Timezone';
import { toLuxonDate } from '../../../../utils/dateHelpers';

export type DateTimeTzSize = 'small' | 'medium';
const useStyles = makeStyles<{ size: DateTimeTzSize }>()((theme, { size }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    margin: theme.spacing(2, 0, 1),
    justifyContent: 'space-between',
    maxWidth: 300
  },
  timezone: {
    minWidth: 80
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: size === 'small' ? theme.typography.htmlFontSize * 0.65 : theme.typography.caption.fontSize
  },
  disabledText: {
    color: theme.palette.text.disabled,
    fontSize: size === 'small' ? theme.typography.caption.fontSize : theme.typography.body1.fontSize
  }
}));

function formatTimeInTimezone(date: Date | string, timezone: Timezone): string {
  // create date within local time
  const luxonDate = toLuxonDate(date);
  // Prefer the IANA zone so the offset is the one in effect on `date` itself; a numeric offset can
  // only ever describe one side of a DST boundary.
  const zonedDate = timezone.zone
    ? luxonDate.setZone(timezone.zone)
    : luxonDate.plus({ minutes: timezone.offset * 60 - luxonDate.offset });

  // get an int expression of local/zoned date
  const localInt = parseInt(luxonDate.toFormat('yyyyMMdd'));
  const zonedInt = parseInt(zonedDate.toFormat('yyyyMMdd'));

  // formatting time
  const formatted = zonedDate.toFormat('t');

  // print +1 and -1 based on day difference
  if (zonedInt > localInt) return `${formatted} (+1)`;
  if (localInt > zonedInt) return `${formatted} (-1)`;

  return formatted;
}

export const DateTimeTz = ({
  className,
  disabled = false,
  size = 'medium',
  timezones,
  time
}: {
  className?: string;
  disabled?: boolean;
  size?: DateTimeTzSize;
  timezones: Timezone[];
  time?: Date | string;
}): React.ReactElement => {
  const { classes, cx } = useStyles({ size });

  try {
    return (
      <div className={cx(classes.root, className)}>
        {timezones
          .filter((timezone) => !timezone.isLocal)
          .map((tz) => (
            <div key={tz.label} className={classes.timezone}>
              <Typography className={classes.label} variant="caption">
                {tz.label}
              </Typography>
              <Typography className={cx({ [classes.disabledText]: disabled })}>
                {time ? formatTimeInTimezone(time, tz) : '–'}
              </Typography>
            </div>
          ))}
      </div>
    );
  } catch {
    return <></>;
  }
};
